// 진행 점수(D5a §4.3): 충돌 때 "진행이 더 많은 쪽"을 고르는 기준. 클라이언트와 Worker가 같은 함수로 계산한다
// (서버는 클라이언트가 보낸 점수를 쓰지 않고 원문에서 다시 센다). 원문이 깨졌으면 그 항목만 0으로 본다(던지지 않는다).
// 사전식: 1) 전 영웅 누적 EXP 2) 깬 스테이지(노말+하드) 3) 누적 획득 골드 4) 보상 영수증 수 — 모두 정상 플레이에서 줄지 않는 값.
import type { ProgressScore, Snapshot } from './protocol';

type Obj = Record<string, unknown>;
const obj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);
const nat = (v: unknown): number => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : 0);

function json(s: Snapshot, key: string): Obj | null {
  const raw = s.keys[key];
  if (typeof raw !== 'string') return null;
  try { const p: unknown = JSON.parse(raw); return obj(p) ? p : null; } catch { return null; }
}

/** 성장 장부 원문의 영수증 EXP 합(같은 id는 한 번 — growth.ts parseLedger와 같은 규칙) + 접은 합계(version 3 fold.xp, D10 원장 접기) */
export function ledgerXp(ledger: Obj | null): number {
  if (!ledger || !Array.isArray(ledger.grants)) return 0;
  const seen = new Set<string>();
  let sum = obj(ledger.fold) ? nat(ledger.fold.xp) : 0;
  for (const g of ledger.grants) {
    if (!obj(g) || typeof g.id !== 'string' || seen.has(g.id)) continue;
    seen.add(g.id);
    sum += nat(g.xp);
  }
  return Math.min(sum, Number.MAX_SAFE_INTEGER);
}

/** 환생 회차 성장 원장 키(game/rebirth REBIRTH_GROWTH_KEYS와 같아야 한다 — score.test가 확인). 이 파일은 Worker도 써서 게임 모듈을 import하지 않는다 */
export const REBIRTH_GROWTH_SCORE_KEYS: readonly string[] = ['dungeonhero.growth.kael.c1.v1', 'dungeonhero.growth.kael.c2.v1', 'dungeonhero.growth.lyra.c1.v1', 'dungeonhero.growth.lyra.c2.v1'];

export function progressScore(s: Snapshot): ProgressScore {
  // 카엘: v2가 있으면 v2(v1은 이관 원본이라 중복), 없으면 v1
  const kaelV2 = json(s, 'dungeonhero.growth.v2');
  const kael = kaelV2 ? ledgerXp(kaelV2) : ledgerXp(json(s, 'dungeonhero.growth.v1'));
  let xp = kael + ledgerXp(json(s, 'dungeonhero.growth.lyra.v1'));
  // 환생 회차 원장(game/rebirth REBIRTH_GROWTH_KEYS): 환생 뒤 판도 진행 점수를 올려야 충돌 때 더 진행한 쪽을 고른다(C6에서 발견 — 전에는 빠져 있었다)
  for (const k of REBIRTH_GROWTH_SCORE_KEYS) xp += ledgerXp(json(s, k));
  xp = Math.min(xp, Number.MAX_SAFE_INTEGER);
  const stages = nat(json(s, 'dungeonhero.stage.v1')?.cleared) + nat(json(s, 'dungeonhero.stage.hard.v1')?.cleared)
    + nat(json(s, 'dungeonhero.stage.hell.v1')?.cleared);
  const save = json(s, 'dungeonhero.save.v1');
  const gold = nat(save?.gold);
  const rr = save?.rewardResults;
  // 보상 영수증 수 = 남은 영수증 + 접어서 지운 수(save.v1 rewardResults.folded, D10) — 접기가 점수를 줄이지 않게
  const receipts = obj(rr) && obj(rr.entries) ? Object.keys(rr.entries).length + nat(rr.folded) : 0;
  return { xp, stages, gold, receipts };
}

/** a가 더 많으면 양수, 같으면 0, 적으면 음수 */
export function compareScore(a: ProgressScore, b: ProgressScore): number {
  return Math.sign(a.xp - b.xp) || Math.sign(a.stages - b.stages) || Math.sign(a.gold - b.gold) || Math.sign(a.receipts - b.receipts);
}

export const ZERO_SCORE: ProgressScore = Object.freeze({ xp: 0, stages: 0, gold: 0, receipts: 0 });
